import React, { useEffect, useMemo, useState } from "react";
import getDataFromApi from "../../getDataFromApi";
import {
  AccuracyIcon2,
  AnalyticsIcon,
  CodeIcon,
  ImagesIcon,
} from "../../../views/Common/SvgIcons/WorkflowIcons";
import "../../Loader.scss";
import "./AiInsights.scss";

const AI_INSIGHTS_GENERATING_MESSAGE = "AI Insights generation is in progress.";

const AnimatedText = ({ text }) => (
  <p className="animated-text2 m-0 p-0">
    {text.split("").map((character, index) => (
      character === " " ? (
        <span
          key={`space-${index}`}
          aria-hidden="true"
          style={{ display: "inline-block", width: "10px" }}
        />
      ) : (
        <span
          key={`${character}-${index}`}
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          {character}
        </span>
      )
    ))}
  </p>
);

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const firstDefined = (source, keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key];
    }
  }
  return undefined;
};

const hasContent = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (isObject(value)) return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== "";
};

const firstWithContent = (...values) => values.find(hasContent);

const extractJsonField = (source, key) => {
  const match = new RegExp(`"${key}"\\s*:`).exec(source);
  if (!match) return undefined;

  let start = match.index + match[0].length;
  while (/\s/.test(source[start] || "")) start += 1;

  const opening = source[start];
  if (opening === '"') {
    let escaped = false;
    for (let index = start + 1; index < source.length; index += 1) {
      const character = source[index];
      if (character === '"' && !escaped) {
        try {
          return JSON.parse(source.slice(start, index + 1));
        } catch {
          return undefined;
        }
      }
      escaped = character === "\\" && !escaped;
      if (character !== "\\") escaped = false;
    }
    return undefined;
  }

  if (opening !== "[" && opening !== "{") return undefined;

  const closing = opening === "[" ? "]" : "}";
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let index = start; index < source.length; index += 1) {
    const character = source[index];
    if (inString) {
      if (character === '"' && !escaped) inString = false;
      escaped = character === "\\" && !escaped;
      if (character !== "\\") escaped = false;
      continue;
    }

    if (character === '"') {
      inString = true;
    } else if (character === opening) {
      depth += 1;
    } else if (character === closing) {
      depth -= 1;
      if (depth === 0) {
        try {
          return JSON.parse(source.slice(start, index + 1));
        } catch {
          return undefined;
        }
      }
    }
  }

  return undefined;
};

const parseEmbeddedReport = (value) => {
  if (isObject(value)) return value;
  if (typeof value !== "string") return null;

  const source = value
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "")
    .trim();

  try {
    const parsed = JSON.parse(source);
    if (isObject(parsed)) return parsed;
  } catch {
    // The API can return a truncated trailing summary. Complete fields above
    // it are still valid and are extracted independently below.
  }

  const extracted = {};
  [
    "overall_report",
    "ranked_tips",
    "augmentation_recipe",
    "data_collection_focus",
    "summary",
  ].forEach((key) => {
    const fieldValue = extractJsonField(source, key);
    if (fieldValue !== undefined) extracted[key] = fieldValue;
  });

  return Object.keys(extracted).length ? extracted : null;
};

const unwrapInsights = (response) => {
  const payload = firstDefined(response, [
    "llm_report",
    "insight_report",
    "ai_insights",
    "ai_insight",
    "insights",
    "report",
    "result",
    "data",
  ]) || response || {};

  return parseEmbeddedReport(payload) || payload;
};

const toItems = (value) => {
  if (Array.isArray(value)) return value;
  if (isObject(value)) return Object.entries(value).map(([key, item]) => (
    isObject(item) ? { title: key, ...item } : { title: key, value: item }
  ));
  return value == null || value === "" ? [] : [value];
};

const readableLabel = (value = "") =>
  String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const renderText = (value) => {
  if (typeof value === "string" || typeof value === "number") return value;
  if (!isObject(value)) return "";

  return firstDefined(value, [
    "description",
    "suggestion",
    "text",
    "value",
    "recommendation",
  ]) || Object.values(value).filter((item) => typeof item !== "object").join(" — ");
};

const formatParams = (params) => {
  if (!isObject(params)) return renderText(params);

  return Object.entries(params)
    .map(([key, value]) => `${readableLabel(key)}: ${value}`)
    .join(", ");
};

const formatScore = (score) => {
  const numericScore = Number(score);
  return Number.isFinite(numericScore) ? numericScore.toFixed(2) : score;
};

const resolveImageUrl = (source) => {
  if (!source || typeof source !== "string") return "";
  if (/^(https?:|data:|blob:)/i.test(source)) return source;

  const apiUrl = window?.ENV?.API_URL?.replace(/\/$/, "") || "";
  return `${apiUrl}${source.startsWith("/") ? "" : "/"}${source}`;
};

export default function AiInsights({ evaluationId, status, llmReportAvailable }) {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isComplete = String(status || "").toLowerCase() === "complete";

  useEffect(() => {
    setResponse(null);
    setError("");

    if (!evaluationId || !isComplete || !llmReportAvailable) return undefined;

    const controller = new AbortController();
    const filters = encodeURIComponent(JSON.stringify({ _id: evaluationId }));
    const url = `${window.ENV.API_URL}/evaluations?filters=${filters}&section=llm_report`;

    setLoading(true);
    getDataFromApi(url, "GET", undefined, undefined, controller.signal)
      .then((data) => setResponse(data))
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(requestError?.message || "Unable to load AI insights.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [evaluationId, isComplete, llmReportAvailable]);

  const insights = useMemo(() => unwrapInsights(response), [response]);
  const rawOverallReport = firstDefined(insights, [
    "overall_report",
    "overallReport",
    "summary",
  ]);
  const trainingSuggestions = firstDefined(insights, [
    "training_suggestions",
    "trainingSuggestions",
    "suggestions",
  ]);
  const embeddedReport = useMemo(
    () => parseEmbeddedReport(rawOverallReport) ||
      parseEmbeddedReport(trainingSuggestions?.summary),
    [rawOverallReport, trainingSuggestions]
  );
  const overallReport = embeddedReport?.overall_report || rawOverallReport;
  const suggestions = toItems(firstWithContent(
    isObject(trainingSuggestions)
      ? firstDefined(trainingSuggestions, ["ranked_tips", "rankedTips", "tips"])
      : trainingSuggestions,
    embeddedReport?.ranked_tips,
    embeddedReport?.rankedTips
  ));
  const augmentation = toItems(firstWithContent(
    firstDefined(insights, [
      "augmentation_recipe",
      "augmentationRecipe",
      "augmentations",
    ]),
    firstDefined(trainingSuggestions, [
      "augmentation_recipe",
      "augmentationRecipe",
      "augmentations",
    ]),
    embeddedReport?.augmentation_recipe,
    embeddedReport?.augmentationRecipe
  ));
  const hardExamples = toItems(firstWithContent(firstDefined(insights, [
    "hard_examples",
    "hardExamples",
    "low_confidence_examples",
    "misclassified_examples",
    "hard_example_images",
    "hardExampleImages",
  ])));
  const hasInsights = Boolean(overallReport) || suggestions.length > 0 ||
    augmentation.length > 0 || hardExamples.length > 0;

  if (!isComplete) {
    return <div className="ai-insights-state">AI insights will be available when the evaluation is complete.</div>;
  }

  if (!llmReportAvailable) {
    return (
      <div className="ai-insights-state">
        <AnimatedText text={AI_INSIGHTS_GENERATING_MESSAGE} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ai-insights-state">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        Generating AI insights…
      </div>
    );
  }

  if (error) return <div className="ai-insights-state text-danger">{error}</div>;
  if (!hasInsights) return <div className="ai-insights-state">No AI insights are available for this evaluation.</div>;

  return (
    <div className="ai-insights-grid">
      <article className="ai-insights-card">
        <h6><CodeIcon className="wf-icon-small text-primary" />1. Overall Report</h6>
        <div className="ai-insights-card__body">
          <p className="ai-insights-report">{renderText(overallReport)}</p>
        </div>
      </article>

      <article className="ai-insights-card">
        <h6><AccuracyIcon2 className="wf-icon-small text-primary" />2. Training Suggestions</h6>
        <div className="ai-insights-card__body">
          <ol className="ai-insights-numbered-list">
            {suggestions.map((suggestion, index) => (
              <li key={`suggestion-${index}`}>
                <span>
                  {isObject(suggestion) && suggestion.title && (
                    <strong>{readableLabel(suggestion.title)}: </strong>
                  )}
                  {isObject(suggestion)
                    ? firstDefined(suggestion, ["rationale", "description", "suggestion", "text"])
                    : renderText(suggestion)}
                  {isObject(suggestion) && suggestion.action && (
                    <small className="ai-insights-action"><strong>Action:</strong> {suggestion.action}</small>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </article>

      <article className="ai-insights-card">
        <h6><AnalyticsIcon className="wf-icon-small text-primary" />3. Augmentation Recipe</h6>
        <div className="ai-insights-card__body">
          <ul className="ai-insights-recipe">
            {augmentation.map((item, index) => (
              <li key={`augmentation-${index}`}>
                {isObject(item) && (item.name || item.title) && (
                  <strong>{readableLabel(item.name || item.title)}: </strong>
                )}
                {isObject(item) && item.params ? formatParams(item.params) : renderText(item)}
                {isObject(item) && item.why && <small className="ai-insights-action">{item.why}</small>}
              </li>
            ))}
          </ul>
        </div>
      </article>

      <article className="ai-insights-card">
        <h6><ImagesIcon className="wf-icon-small text-primary" />4. Hard Examples (Low Confidence / Misclassified)</h6>
        <div className="ai-insights-card__body ai-insights-examples">
          {hardExamples.map((example, index) => {
            const item = isObject(example) ? example : { name: String(example) };
            const imageSource = firstDefined(item, ["thumbnail", "image_url", "image_path", "image", "path"]);
            const name = firstDefined(item, ["image_name", "filename", "file_name", "name", "title"]);
            const reason = firstDefined(item, ["reason", "status", "label", "tag", "category"]) ||
              (item.misclassified === true ? "misclassified" : "low confidence");
            const score = firstDefined(item, ["confidence", "confidence_score", "score", "value"]);

            return (
              <div className="ai-insights-example" key={`${name || "example"}-${index}`}>
                {imageSource ? (
                  <img src={resolveImageUrl(imageSource)} alt="" />
                ) : (
                  <span className="ai-insights-image-placeholder"><ImagesIcon /></span>
                )}
                <span className="ai-insights-example-name">{name || `Example ${index + 1}`}</span>
                {reason && <span className={`ai-insights-tag ${String(reason).toLowerCase().includes("confidence") ? "low" : ""}`}>{readableLabel(reason)}</span>}
                {score !== undefined && <span className="ai-insights-score">{formatScore(score)}</span>}
              </div>
            );
          })}
        </div>
      </article>
    </div>
  );
}
